/**
 * Standardized error handling utilities
 */

import * as Sentry from '@sentry/react'

/**
 * Format API error into consistent structure
 * @param {Error} error - Axios error object
 * @returns {Object} Formatted error with message, status, and data
 */
export const formatApiError = (error) => {
  if (error.response) {
    // Server responded with error status
    return {
      message: error.response.data?.detail ||
               error.response.data?.message ||
               (Array.isArray(error.response.data?.non_field_errors)
                 ? error.response.data.non_field_errors[0]
                 : error.response.data?.non_field_errors) ||
               error.response.statusText ||
               'An error occurred',
      status: error.response.status,
      data: error.response.data,
      field_errors: error.response.data?.errors || error.response.data,
    };
  } else if (error.request) {
    // Request made but no response received
    return {
      message: 'Network error. Please check your connection.',
      status: null,
      data: null,
    };
  } else {
    // Error setting up request
    return {
      message: error.message || 'An unexpected error occurred',
      status: null,
      data: null,
    };
  }
};

/**
 * Log error in development, send to monitoring in production
 * @param {string} context - Where the error occurred
 * @param {Error} error - The error object
 */
export const logError = (context, error) => {
  if (import.meta.env.DEV) {
    console.error(`[${context}]`, error);
  } else {
    Sentry.captureException(error, { tags: { context } });
  }
};

/**
 * Wrap an async API call with standardised error handling.
 * Logs the error and re-throws a formatted version.
 *
 * @param {string} label - Human-readable context for logs
 * @param {Function} fn  - Async function to execute
 * @returns {Promise<*>} The resolved value of fn()
 */
export const withApiError = async (label, fn) => {
  try {
    return await fn();
  } catch (error) {
    logError(label, error);
    throw formatApiError(error);
  }
};
